import { createFileRoute, Link } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/cheap-website-true-cost-colorado";
const TITLE =
  "What Does a Cheap Website Actually Cost You in Colorado? (2026 Honest Math)";
const DESCRIPTION =
  "The hidden cost of a $300 template website for a Colorado business — lost leads, lower close rate, and the SEO debt you pay back in year two.";

export const Route = createFileRoute("/learn/cheap-website-true-cost-colorado")({
  head: () => learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="Pricing"
      title={TITLE}
      intro="A $300 Wix template feels cheap until you do the math on lost leads, slower conversions, and the SEO debt you'll pay back in year two. Here's what cheap websites actually cost a Colorado business."
      readTime="5 min read"
      publishedDate="2026-06-22"
      path={PATH}
      definition={{
        term: "What's the real cost of a cheap website for a Colorado business?",
        body: "A $300–$800 template website typically costs a Colorado service business $15,000–$60,000+ per year in lost leads vs. a real custom site. Conversion rates run 30–60% lower, Google rankings stall, and most owners end up rebuilding within 18 months. Our Pro Website package is $1,850 one-time — designed to pay for itself in 1–3 jobs.",
      }}
      toc={[
        { id: "the-real-math", label: "The real math on a cheap site" },
        { id: "what-cheap-sites-cost-you", label: "What cheap sites actually cost" },
        { id: "the-rebuild-tax", label: "The 18-month rebuild tax" },
        { id: "our-pricing", label: "What we charge and why" },
        { id: "when-cheap-is-fine", label: "When a cheap site IS fine" },
      ]}
      faqs={[
        {
          q: "Can't I just use Wix or Squarespace for $300?",
          a: "You can. But Wix/Squarespace sites in 2026 typically convert 30–50% worse than a custom-built site because of slow load times, generic templates, and weak schema. For a Colorado service business doing $300k+ in revenue, that conversion gap costs more per year than a real site cost one-time.",
        },
        {
          q: "What's actually slow about template websites?",
          a: "Bundled JavaScript, render-blocking widgets, and stock images. Real-world load times on Wix/Squarespace average 3.5–6 seconds on mobile in Colorado markets. Google penalizes anything over 2.5 seconds for ranking — and users abandon at the same rate.",
        },
        {
          q: "Do I really need a custom website if I have good Google reviews?",
          a: "Reviews get you the click. The website gets you the call. A great GBP into a bad website loses 40–60% of qualified visitors at the site itself — you paid for the click and threw it away.",
        },
        {
          q: "How fast does a $1,850 custom site pay back?",
          a: "For most Colorado service businesses with average tickets above $300, the site pays back in 1–3 booked jobs from organic and direct traffic — usually inside 30–60 days of launch.",
        },
      ]}
    >
      <h2 id="the-real-math">The real math on a cheap site</h2>
      <p>
        Pick a typical Colorado service business: 500 monthly visitors, $400
        average job, 35% close rate on calls.
      </p>
      <ul>
        <li><strong>Custom site:</strong> 7% visitor-to-call rate × 35% close = ~12 jobs/month = $4,800 revenue.</li>
        <li><strong>Cheap template:</strong> 3% visitor-to-call rate × 35% close = ~5 jobs/month = $2,000 revenue.</li>
      </ul>
      <p>
        Same traffic, $2,800/month gap. That's $33,600/year of revenue the
        template site costs you — to save $2,000 one-time.
      </p>

      <h2 id="what-cheap-sites-cost-you">What cheap sites actually cost</h2>
      <ul>
        <li><strong>Lower conversion rate</strong> — 30–60% fewer leads per visitor.</li>
        <li><strong>Worse SEO</strong> — generic schema, no content depth, no real internal linking.</li>
        <li><strong>Slower load</strong> — Wix/Squarespace average 3.5–6s on mobile.</li>
        <li><strong>Weaker trust signals</strong> — stock photos, template layouts, generic copy.</li>
        <li><strong>Higher PPC costs</strong> — Google Quality Score drops, raising your CPC 15–40%.</li>
        <li><strong>No ownership</strong> — you can't easily move off the platform.</li>
      </ul>

      <h2 id="the-rebuild-tax">The 18-month rebuild tax</h2>
      <p>
        We rebuild a template site for a Colorado client roughly every other
        week. The pattern is always the same: they launched cheap, hit a ceiling
        at month 8–12, tried to add SEO on top, and discovered the platform
        wouldn't support it. They spent the $300 — then spent $3,000–$8,000 on
        a rebuild within 18 months.
      </p>
      <p>
        Net cost: the cheap site, plus the rebuild, plus all the lost leads in
        between.
      </p>

      <h2 id="our-pricing">What we charge and why</h2>
      <p>
        Our Starter Website package is <strong>$1,850 one-time</strong>:
      </p>
      <ul>
        <li>Up to 10 custom-designed pages</li>
        <li>Mobile-first, sub-2-second load times</li>
        <li>SEO foundation (titles, meta, schema, sitemap)</li>
        <li>Lead form + click-to-call buttons</li>
        <li>1 year of Cloudflare hosting + SSL</li>
        <li>Full GitHub repo access — you own the code</li>
        <li>7–10 days from kickoff to launch</li>
      </ul>
      <p>
        <Link to="/pricing" className="text-primary underline">See full pricing</Link>{" "}
        and our{" "}
        <Link to="/learn/web-design-cost-colorado" className="text-primary underline">
          full web design cost breakdown
        </Link>
        .
      </p>

      <h2 id="when-cheap-is-fine">When a cheap site IS fine</h2>
      <p>
        Honest carve-out: a $300 Squarespace site is fine for a brand-new
        side-hustle testing a single offer, a hobby blog, a vanity portfolio,
        or a single-event landing page. If you're an established Colorado
        business with revenue, customers, and a phone that should be ringing —
        the cheap site is the most expensive thing you can buy.
      </p>
    </LearnArticleLayout>
  );
}
